// ToolbarPlugin.tsx
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  $createParagraphNode,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useState } from 'react';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createHeadingNode, HeadingTagType } from '@lexical/rich-text';
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  Pilcrow,
  List,
  ListOrdered,
  ListX,
  Undo,
  Redo,
} from "lucide-react";
import { $setBlocksType } from "@lexical/selection";

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  const formatText = (formatType: 'bold' | 'italic' | 'underline') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType);
  };

  const toggleLink = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const isLink = selection.getNodes().some(node => $isLinkNode(node));
        if (isLink) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null); // Remove link
        } else {
          const url = window.prompt("Enter URL");
          if (url) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
          }
        }
      }
    });
  };

  const insertHeading = (tag: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      console.log("Before heading change:", selection);
      $setBlocksType(selection, () => $createHeadingNode(tag));
      // Optionally, log the editor state after the change
      setTimeout(() => {
        editor.getEditorState().read(() => {
          console.log("After heading change:", $getSelection());
        });
      }, 0);
    });
  };

  const insertParagraph = () => {
    editor.update(() => {
      $setBlocksType($getSelection(), () => $createParagraphNode());
    });
  };

  const insertUnorderedList = () => {
    console.log("Inserting unordered list");
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    setTimeout(() => {
      editor.getEditorState().read(() => {
        console.log("After unordered list:", $getSelection());
      });
    }, 0);
  };

  const insertOrderedList = () => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  const removeList = () => {
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
  };

  return (
    <div className="flex gap-3 px-2 py-2 border-b bg-gray-100 text-sm">
      <button onClick={() => formatText('bold')} title="Bold">
        <Bold className="w-4 h-4" />
      </button>
      <button onClick={() => formatText('italic')} title="Italic">
        <Italic className="w-4 h-4" />
      </button>
      <button onClick={() => formatText('underline')} title="Underline">
        <Underline className="w-4 h-4" />
      </button>
      <button onClick={toggleLink} title="Link">
        <LinkIcon className="w-4 h-4" />
      </button>
      {/* <button onClick={() => insertHeading('h1')} title="Heading 1">
        <Heading1 className="w-4 h-4" />
      </button>
      <button onClick={() => insertHeading('h2')} title="Heading 2">
        <Heading2 className="w-4 h-4" />
      </button>
      <button onClick={() => insertHeading('h3')} title="Heading 3">
        <Heading3 className="w-4 h-4" />
      </button>
      <button onClick={insertParagraph} title="Paragraph">
        <Pilcrow className="w-4 h-4" />
      </button>
      <button onClick={insertUnorderedList} title="Bulleted List">
        <List className="w-4 h-4" />
      </button>
      <button onClick={insertOrderedList} title="Numbered List">
        <ListOrdered className="w-4 h-4" />
      </button>
      <button onClick={removeList} title="Remove List">
        <ListX className="w-4 h-4" />
      </button> */}
      <button onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} disabled={!canUndo} title="Undo">
        <Undo className="w-4 h-4" />
      </button>
      <button onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} disabled={!canRedo} title="Redo">
        <Redo className="w-4 h-4" />
      </button>
    </div>
  );
}
